import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GitBranch, CheckCircle2, XCircle, AlertTriangle, UserPlus, KeyRound, Copy, Search,
} from 'lucide-react';
import { forgejo } from '../api';

/**
 * Das Git-Konto einer Person — direkt in der Benutzerverwaltung.
 *
 * ══ WARUM HIER UND NICHT NUR AUF DER GIT-SEITE ═════════════════════════════
 *
 * Weil hier der Moment ist, in dem jemand über diese Person nachdenkt. Ein
 * neuer Kollege wird angelegt, und der Zugang zum Quelltext gehört zum selben
 * Vorgang — nicht zu einem zweiten, an den man sich erinnern muss. Die
 * Git-Seite bleibt für den Blick über alle hinweg.
 *
 * ══ VORHANDENE KONTEN WERDEN ÜBERNOMMEN ════════════════════════════════════
 *
 * Steht in Forgejo schon ein Konto mit derselben Mailadresse — von Hand
 * angelegt, aus einer früheren Installation —, wird es verknüpft statt ein
 * zweites anzulegen. Die Instanz ist frisch, das sollte selten sein; „selten"
 * ist aber kein Grund, den Fall als Fehlermeldung enden zu lassen.
 */
export default function GitKontoBereich({
  userId,
  email,
  pcStatus,
}: {
  userId: string;
  email: string;
  pcStatus: string;
}) {
  const qc = useQueryClient();
  const [meldung, setMeldung] = useState<{ art: 'gut' | 'fehler'; text: string } | null>(null);
  const [kennwort, setKennwort] = useState('');
  const [aendernErzwingen, setAendernErzwingen] = useState(true);
  const [zeigeKennwort, setZeigeKennwort] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const [gefunden, setGefunden] = useState<string | null>(null);

  const status = useQuery({ queryKey: ['forgejo', 'status'], queryFn: forgejo.status });
  const konten = useQuery({
    queryKey: ['forgejo', 'konten'],
    queryFn: forgejo.konten,
    enabled: status.data?.eingerichtet === true,
  });

  const zeile = konten.data?.zeilen.find((z) => z.userId === userId) ?? null;
  const konto = zeile?.konto ?? null;

  const nachAktion = (e: { ok: boolean; meldung: string }) => {
    setMeldung({ art: e.ok ? 'gut' : 'fehler', text: e.meldung });
    qc.invalidateQueries({ queryKey: ['forgejo'] });
  };

  const anlegen = useMutation({ mutationFn: () => forgejo.anlegen(userId), onSuccess: nachAktion });
  const sperren = useMutation({ mutationFn: () => forgejo.sperren(userId), onSuccess: nachAktion });
  const entsperren = useMutation({
    mutationFn: () => forgejo.entsperren(userId),
    onSuccess: nachAktion,
  });
  const kennwortSetzen = useMutation({
    mutationFn: () => forgejo.kennwort(userId, kennwort, aendernErzwingen),
    onSuccess: (e) => {
      nachAktion(e);
      if (e.ok) setZeigeKennwort(false);
    },
  });
  const suchen = useMutation({
    mutationFn: () => forgejo.suche(email),
    onSuccess: (e) => {
      setGefunden(e.gefunden ? (e.konto?.benutzername ?? null) : null);
      setMeldung({
        art: 'gut',
        text: e.gefunden
          ? `In Forgejo gibt es „${e.konto?.benutzername}" mit dieser Adresse — Anlegen übernimmt es.`
          : 'Unter dieser Adresse gibt es dort noch kein Konto.',
      });
    },
  });

  const laeuft =
    anlegen.isPending ||
    sperren.isPending ||
    entsperren.isPending ||
    kennwortSetzen.isPending ||
    suchen.isPending;

  if (!status.data?.eingerichtet) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <GitBranch size={14} /> Git-Konto
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Forgejo ist nicht eingerichtet — unter Einstellungen fehlen Adresse oder Token.
        </p>
      </div>
    );
  }

  function erzeugen() {
    // 18 Byte, base64url: lang genug, und kein Zeichen, das beim Kopieren in
    // ein Terminal zum Problem wird.
    const zufall = new Uint8Array(18);
    crypto.getRandomValues(zufall);
    const wert = btoa(String.fromCharCode(...zufall))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    setKennwort(wert);
    setZeigeKennwort(true);
  }

  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <GitBranch size={14} /> Git-Konto
        </div>
        {konto ? (
          <span
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${
              konto.aktiv ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
            }`}
          >
            {konto.aktiv ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
            {konto.aktiv ? 'offen' : 'gesperrt'}
          </span>
        ) : (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
            keines
          </span>
        )}
      </div>

      {zeile?.abweichung && (
        <p className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-700">
          <AlertTriangle size={11} className="mt-px shrink-0" /> {zeile.abweichung}
        </p>
      )}

      {konto ? (
        <>
          <p className="mt-1.5 font-mono text-[11px] text-slate-600">{konto.benutzername}</p>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {konto.aktiv ? (
              <button
                type="button"
                onClick={() => sperren.mutate()}
                disabled={laeuft}
                className="rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                sperren
              </button>
            ) : (
              <button
                type="button"
                onClick={() => entsperren.mutate()}
                disabled={laeuft}
                className="rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                entsperren
              </button>
            )}
            <button
              type="button"
              onClick={() => setZeigeKennwort((v) => !v)}
              disabled={laeuft}
              className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <KeyRound size={11} /> Kennwort setzen
            </button>
          </div>

          {zeigeKennwort && (
            <div className="mt-2 space-y-2 rounded border border-slate-200 bg-slate-50 p-2">
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={kennwort}
                  onChange={(e) => setKennwort(e.target.value)}
                  placeholder="mindestens 8 Zeichen"
                  className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-[11px]"
                />
                <button
                  type="button"
                  onClick={erzeugen}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
                >
                  erzeugen
                </button>
                {kennwort && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(kennwort);
                      setKopiert(true);
                      window.setTimeout(() => setKopiert(false), 2000);
                    }}
                    className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Copy size={10} /> {kopiert ? 'kopiert' : 'kopieren'}
                  </button>
                )}
              </div>
              <label className="flex items-start gap-1.5 text-[11px] text-slate-600">
                <input
                  type="checkbox"
                  checked={aendernErzwingen}
                  onChange={(e) => setAendernErzwingen(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  beim ersten Anmelden ändern lassen
                  <span className="block text-[10px] text-slate-500">
                    Bis das geschehen ist, weisen Git über HTTPS und die API die Anmeldung ab —
                    die Weboberfläche geht. Für jemanden, der sofort pushen will, besser aus.
                  </span>
                </span>
              </label>
              <button
                type="button"
                onClick={() => kennwortSetzen.mutate()}
                disabled={laeuft || kennwort.length < 8}
                className="w-full rounded bg-brand-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
              >
                {kennwortSetzen.isPending ? 'wird gesetzt …' : 'Kennwort setzen'}
              </button>
              <p className="text-[10px] leading-relaxed text-slate-500">
                Wird nicht gespeichert: es geht einmal nach Forgejo und einmal an dich. Notieren,
                bevor der Dialog zugeht — nachschlagen lässt es sich nicht, nur neu setzen.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => anlegen.mutate()}
            disabled={laeuft || pcStatus !== 'ACTIVE'}
            title={
              pcStatus === 'ACTIVE'
                ? 'Legt ein Konto an — oder übernimmt ein vorhandenes mit derselben Adresse'
                : 'Für einen gesperrten Nutzer wird kein Git-Konto angelegt'
            }
            className="inline-flex items-center gap-1 rounded bg-brand-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-40"
          >
            <UserPlus size={11} /> {anlegen.isPending ? 'wird angelegt …' : 'Konto anlegen'}
          </button>
          <button
            type="button"
            onClick={() => suchen.mutate()}
            disabled={laeuft}
            title="Sieht nach, ob es in Forgejo schon ein Konto mit dieser Adresse gibt"
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <Search size={11} /> {suchen.isPending ? 'sucht …' : 'in Forgejo suchen'}
          </button>
        </div>
      )}

      {gefunden && !konto && (
        <p className="mt-1.5 text-[11px] text-slate-600">
          Vorhanden als <span className="font-mono">{gefunden}</span> — „Konto anlegen" verknüpft
          es, statt ein zweites anzulegen.
        </p>
      )}

      {meldung && (
        <p
          className={`mt-1.5 text-[11px] ${
            meldung.art === 'gut' ? 'text-emerald-700' : 'text-rose-700'
          }`}
        >
          {meldung.text}
        </p>
      )}
    </div>
  );
}
